import { useCallback } from "react";
import { type ApolloError, type FetchResult } from "@apollo/client";
import {
  UpdateCartProps,
  UpdateCartData,
  useUpdateCart,
} from "src/hooks/useUpdateCart";
import { CartUpdateAction } from "src/types/ctgraphql.d";
import { CartInfo } from "src/types/cartInfo";

interface DeleteCartItemHook {
  data?: UpdateCartData;
  loading: boolean;
  error?: ApolloError;
  deleteCartItem: (
    lineItemIds: string[],
    cartInfo: CartInfo
  ) => Promise<FetchResult<UpdateCartData> | undefined>;
}

function useDeleteCartItem({
  refetchQueries = [],
  onCompleted,
}: UpdateCartProps = {}): DeleteCartItemHook {
  const { updateCart, loading, error } = useUpdateCart({
    refetchQueries,
    onCompleted: (updatedCart) => {
      if (onCompleted) {
        onCompleted(updatedCart);
      }
    },
  });

  const deleteCartItem: DeleteCartItemHook["deleteCartItem"] = useCallback(
    async (lineItemIds: string[], cartInfo: CartInfo) => {
      const lineItemsToBeRemoved: CartUpdateAction[] = lineItemIds?.map(
        (lineItemId: string) => ({
          removeLineItem: {
            lineItemId,
          },
        })
      );
      return await updateCart({
        actions: lineItemsToBeRemoved,
        cartInfo,
      });
    },
    [updateCart]
  );

  return {
    deleteCartItem,
    loading,
    error,
  };
}

export { useDeleteCartItem };
